import React, { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '@src/hooks/useClickOutside';
import * as styles from './Popover.module.css';

interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  offset?: number;
  onOpen?: () => void;
  onClose?: () => void;
}

export const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  position = 'top',
  align = 'center',
  offset = 8,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  // Use the custom hook for click outside functionality
  useClickOutside(containerRef, () => {
    if (isOpen) {
      handleClose();
    }
  });

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.trigger} onClick={handleToggle}>
        {trigger}
      </div>
      {isOpen && (
        <div
          className={`${styles.popover} ${styles[position]} ${styles[align]}`}
          style={{ '--offset': `${offset}px` } as React.CSSProperties}
        >
          <div className={styles.content}>{children}</div>
        </div>
      )}
    </div>
  );
};
